import * as Sentry from '@sentry/react';

// Short-window dedup so bursts of identical errors (e.g. 1k+ failed fetches in
// a parallel upload loop) don't blow through the Sentry ingest quota.
const recent = new Map<string, number>();
const DEDUPE_MS = 5000;
const RECENT_MAX = 200;

function keyOf(event: Sentry.Event): string {
  const msg = event.message || event.exception?.values?.[0]?.value || '';
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  const frame = frames && frames.length > 0 ? frames[frames.length - 1] : undefined;
  return `${msg}|${frame?.filename ?? ''}|${frame?.lineno ?? ''}|${frame?.colno ?? ''}`;
}

export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Prefer privacy-first defaults. If you need richer debugging later, we can relax these.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    sampleRate: import.meta.env.PROD ? 0.25 : 1.0,
    tracesSampleRate: import.meta.env.PROD ? 0.05 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    ignoreErrors: [
      'Minified React error #418',
      'Minified React error #423',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /hydrat(e|ion)/i,
    ],
    beforeSend(event) {
      if (import.meta.env.DEV) return null;

      const k = keyOf(event);
      const now = Date.now();
      const last = recent.get(k);
      if (last && now - last < DEDUPE_MS) return null;
      recent.set(k, now);
      if (recent.size > RECENT_MAX) {
        for (const [kk, t] of recent) {
          if (now - t > DEDUPE_MS) recent.delete(kk);
        }
      }

      if (event.request?.headers) {
        delete event.request.headers['authorization'];
        delete event.request.headers['x-api-key'];
      }

      return event;
    },
  });

  return true;
}

export function setSentryUser(user: { id: string; email?: string; name?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function addBreadcrumb(message: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
  });
}


